import { CommissionSightClient } from '@commissionsight/sdk';
import {
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

const BASE_URL = import.meta.env.VITE_API_BASE_URL ?? 'http://localhost:8787/v1';

// Token storage decision: the session token lives in localStorage, NOT an
// httpOnly cookie. The API is a separate origin (api.commissionsight.com) and
// the SDK authenticates with `Authorization: Bearer`, so a cookie would need
// cross-site SameSite=None plus CSRF protection; localStorage keeps this a pure
// SPA with no cookie/CSRF surface. We accept the XSS token-theft tradeoff
// because neither app uses dangerouslySetInnerHTML/eval and deps are minimal.
// Revisit (httpOnly + SameSite cookie + CSRF tokens) if the threat model grows.
const TOKEN_KEY = 'cs.token';
// When a back-office operator impersonates a user, we stash the operator's own
// session (if any) here so "Exit" restores it instead of logging them out.
const IMP_PREV_KEY = 'cs.token.prev';

/**
 * A support-impersonation session is handed off via the URL fragment
 * (`#impersonate=<token>`) — fragments are never sent to a server, so the token
 * stays out of access logs. Consume it once on load: stash any existing session,
 * adopt the impersonation token, and scrub it from the URL/history.
 */
function consumeImpersonationHash(): string | null {
  if (typeof window === 'undefined') return null;
  const m = /[#&]impersonate=([^&]+)/.exec(window.location.hash);
  if (!m?.[1]) return null;
  const token = decodeURIComponent(m[1]);
  const prev = localStorage.getItem(TOKEN_KEY);
  if (prev && prev !== token) localStorage.setItem(IMP_PREV_KEY, prev);
  localStorage.setItem(TOKEN_KEY, token);
  window.history.replaceState(null, '', window.location.pathname + window.location.search);
  return token;
}

function initialToken(): string | null {
  return consumeImpersonationHash() ?? localStorage.getItem(TOKEN_KEY);
}

export interface WorkspaceOption {
  id: string;
  name: string;
  isDefault: boolean;
}

interface AuthState {
  token: string | null;
  baseUrl: string;
  client: CommissionSightClient;
  /** Internal account pause flags (from /v1/me) — true = paused. Drive UX banners. */
  processingPaused: boolean;
  aiPaused: boolean;
  /** Multi-workspace feature + the account's workspaces (from /v1/workspaces). */
  workspacesEnabled: boolean;
  workspaces: WorkspaceOption[];
  /** Re-fetch the account's workspaces (call after creating one so it appears). */
  refreshWorkspaces: () => Promise<void>;
  /** Per-account feature entitlements (from /v1/me) — drive what the app shows. */
  entitlements: Record<string, boolean>;
  /** True when the account has been disabled by CommissionSight (every API call
   *  returns 403 account_disabled). The app shows a blocking notice instead of routes. */
  accountDisabled: boolean;
  /** Read-only support-impersonation session (a CommissionSight operator viewing as this user). */
  impersonating: boolean;
  impersonatedBy: string | null;
  impersonatedEmail: string | null;
  signIn: (token: string) => void;
  signOut: () => void;
  /** End an impersonation session: revoke it server-side and restore the operator's own session. */
  exitImpersonation: () => void;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [token, setToken] = useState<string | null>(initialToken);
  const [paused, setPaused] = useState({ processingPaused: false, aiPaused: false });
  const [imp, setImp] = useState<{ active: boolean; by: string | null; email: string | null }>({
    active: false,
    by: null,
    email: null,
  });
  const [entitlements, setEntitlements] = useState<Record<string, boolean>>({});
  const [accountDisabled, setAccountDisabled] = useState(false);
  const [ws, setWs] = useState<{ enabled: boolean; list: WorkspaceOption[] }>({
    enabled: false,
    list: [],
  });

  // Load the account's pause flags from /v1/me (direct read — these are internal
  // flags, deliberately not part of the SDK surface).
  useEffect(() => {
    if (!token) {
      setPaused({ processingPaused: false, aiPaused: false });
      setImp({ active: false, by: null, email: null });
      setEntitlements({});
      setAccountDisabled(false);
      return;
    }
    let active = true;
    fetch(`${BASE_URL.replace(/\/$/, '')}/me`, { headers: { authorization: `Bearer ${token}` } })
      .then(async (r) => {
        if (r.ok) return r.json();
        // An operator disabled the account: show the blocking notice, not a
        // silent half-broken app.
        const problem = (await r.json().catch(() => null)) as { code?: string } | null;
        if (active && r.status === 403 && problem?.code === 'account_disabled') {
          setAccountDisabled(true);
        }
        return null;
      })
      .then((me) => {
        if (active && me) {
          setPaused({
            processingPaused: !!me.processingPaused,
            aiPaused: !!me.aiPaused,
          });
          setImp({
            active: !!me.impersonating,
            by: me.impersonatedBy ?? null,
            email: me.email ?? null,
          });
          setEntitlements(me.entitlements ?? {});
        }
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, [token]);

  // Load the account's workspaces (drives the upload target picker, the analytics
  // switcher, and the customer-facing workspace manager). Exposed as
  // `refreshWorkspaces` so a just-created workspace shows up without a reload.
  const refreshWorkspaces = useCallback(async () => {
    if (!token) {
      setWs({ enabled: false, list: [] });
      return;
    }
    try {
      const r = await fetch(`${BASE_URL.replace(/\/$/, '')}/workspaces`, {
        headers: { authorization: `Bearer ${token}` },
      });
      const res = r.ok ? await r.json() : null;
      if (res) setWs({ enabled: !!res.enabled, list: res.workspaces ?? [] });
    } catch {
      // Transient failure — keep the last-known workspaces rather than wiping them.
    }
  }, [token]);

  useEffect(() => {
    refreshWorkspaces();
  }, [refreshWorkspaces]);

  const value = useMemo<AuthState>(() => {
    const client = new CommissionSightClient({ baseUrl: BASE_URL, token: token ?? undefined });
    return {
      token,
      baseUrl: BASE_URL,
      client,
      processingPaused: paused.processingPaused,
      aiPaused: paused.aiPaused,
      workspacesEnabled: ws.enabled,
      workspaces: ws.list,
      refreshWorkspaces,
      entitlements,
      accountDisabled,
      impersonating: imp.active,
      impersonatedBy: imp.by,
      impersonatedEmail: imp.email,
      signIn: (t: string) => {
        localStorage.setItem(TOKEN_KEY, t);
        setToken(t);
      },
      signOut: () => {
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem(IMP_PREV_KEY);
        setToken(null);
      },
      exitImpersonation: () => {
        // Revoke the impersonation session server-side (best-effort), then restore
        // the operator's own session if we stashed one, else sign out.
        if (token) {
          fetch(`${BASE_URL.replace(/\/$/, '')}/auth/logout`, {
            method: 'POST',
            headers: { authorization: `Bearer ${token}` },
          }).catch(() => {});
        }
        const prev = localStorage.getItem(IMP_PREV_KEY);
        localStorage.removeItem(IMP_PREV_KEY);
        if (prev) {
          localStorage.setItem(TOKEN_KEY, prev);
          setToken(prev);
        } else {
          localStorage.removeItem(TOKEN_KEY);
          setToken(null);
        }
      },
    };
  }, [token, paused, ws, imp, entitlements, accountDisabled, refreshWorkspaces]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
